import React, { useState, useMemo } from "react";
import SEO from "../components/SEO";
import { FAQS, FAQ_CATEGORIES } from "../lib/constants";
import { ChevronDown } from "lucide-react";

export default function FAQPage() {
  const [openIdx, setOpenIdx] = useState(0);
  const [activeCat, setActiveCat] = useState("All");

  const filtered = useMemo(() => {
    if (activeCat === "All") return FAQS;
    return FAQS.filter((f) => f.cat === activeCat);
  }, [activeCat]);

  return (
    <>
      <SEO
        title="FAQ · Medical Cannabis in Washington, DC | District Wellness Center"
        description="Complete FAQ for District Wellness Center — medical cannabis registration, self-certification, hours, delivery, pickup, products, terpenes, edibles and DC-specific rules. 30+ answers for DC residents and out-of-state visitors."
        path="/faq"
        breadcrumbs={[{ name: "FAQ", path: "/faq" }]}
        faq={FAQS.map((f) => ({ q: f.q, a: f.a }))}
      />

      {/* HERO */}
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">FAQ</div>
          <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            Everything you want<br/>to know about <em className="text-brand">DWC</em>.
          </h1>
          <p className="mt-6 text-ink-muted text-lg max-w-2xl leading-relaxed">
            {FAQS.length} answers covering registration, hours, products, delivery,
            edibles, terpenes, DC medical cannabis rules and more.
          </p>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="bg-[#FAFAFA] py-6 border-b border-black/5 sticky top-24 sm:top-28 z-30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap gap-2" data-testid="faq-category-filter">
          {["All", ...FAQ_CATEGORIES].map((cat) => {
            const isActive = activeCat === cat;
            return (
              <button
                key={cat}
                onClick={() => { setActiveCat(cat); setOpenIdx(-1); }}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border transition-all ${
                  isActive ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-black/10 hover:border-ink hover:text-ink"
                }`}
                data-testid={`faq-filter-${cat.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </section>

      {/* FAQ LIST */}
      <section className="py-20 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-6" data-testid="faq-list">
          {filtered.map((f, i) => {
            const isOpen = openIdx === i;
            return (
              <article
                key={f.q}
                className="border-b border-black/10"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  className="w-full py-6 flex items-start gap-6 text-left group"
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-brand mt-1.5 shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="flex-1 font-serif text-xl sm:text-2xl leading-snug text-ink group-hover:text-brand transition-colors">
                    {f.q}
                  </h3>
                  <ChevronDown
                    size={20}
                    className={`text-ink/40 shrink-0 mt-1 transition-transform ${isOpen ? "rotate-180 text-brand" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div className="pb-8 pl-16 pr-10 text-ink-muted text-base leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* AI-friendly full text summary for crawlers (small print, still readable) */}
      <section className="bg-ink text-white/70 py-10 text-xs" aria-label="AI search summary">
        <div className="max-w-4xl mx-auto px-6">
          <details>
            <summary className="cursor-pointer text-white/90 uppercase tracking-[0.3em] text-[10px] mb-3">
              Quick answers · plain text (for AI assistants)
            </summary>
            <div className="mt-4 space-y-4">
              {FAQS.map((f) => (
                <div key={f.q}>
                  <div className="text-white font-medium">Q: {f.q}</div>
                  <div className="mt-1">A: {f.a}</div>
                </div>
              ))}
            </div>
          </details>
        </div>
      </section>
    </>
  );
}
